import type {
  AlignmentResponse,
  LibraryBook,
  SentenceExplanation,
  StoredBook,
  StoredChapter,
  UploadResponse,
  VocabularyFlashcardDraft,
} from "@japa-reader/contracts";
import JSZip from "jszip";
import { cloudIsConfigured } from "../auth";
import { apiErrorMessage, apiFetch } from "./api";
import { normalizeBookLanguage } from "./book-language";

export type { AlignedCharacter, LibraryBook, StoredBook, StoredChapter } from "@japa-reader/contracts";

const DATABASE_NAME = "japa-reader-library";
const DATABASE_VERSION = 3;
const BOOKS_STORE = "books";
const CHAPTERS_STORE = "chapters";
export const FLASHCARD_DECKS_STORE = "flashcard-decks";
export const FLASHCARD_NOTE_TYPES_STORE = "flashcard-note-types";
export const FLASHCARD_NOTES_STORE = "flashcard-notes";
export const FLASHCARD_CARDS_STORE = "flashcard-cards";
export const FLASHCARD_REVIEWS_STORE = "flashcard-reviews";
export const FLASHCARD_MEDIA_STORE = "flashcard-media";
let databasePromise: Promise<IDBDatabase> | null = null;
let localShareTimer = 0;
let localSharePublishPromise: Promise<void> | null = null;

type SharedBook = Omit<StoredBook, "sourceBlob"> & {
  sourceEntry?: string;
  sourceType?: string;
};

type SharedChapter = Omit<StoredChapter, "audioBlob" | "audioUrl"> & {
  audioEntry?: string;
};

type LocalShareManifest = {
  version: 1;
  books: SharedBook[];
  chapters: SharedChapter[];
};

export function openDatabase(): Promise<IDBDatabase> {
  if (databasePromise) return databasePromise;
  if (typeof indexedDB === "undefined") return Promise.reject(new Error("O armazenamento local não está disponível."));

  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onerror = () => reject(request.error ?? new Error("Não foi possível abrir a biblioteca local."));
    request.onblocked = () => reject(new Error("Feche outras abas do Japa Reader e tente novamente."));
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(BOOKS_STORE)) {
        const books = database.createObjectStore(BOOKS_STORE, { keyPath: "id" });
        books.createIndex("updatedAt", "updatedAt");
      }
      if (!database.objectStoreNames.contains(CHAPTERS_STORE)) {
        const chapters = database.createObjectStore(CHAPTERS_STORE, { keyPath: "id" });
        chapters.createIndex("bookId", "bookId");
        chapters.createIndex("bookOrder", ["bookId", "order"]);
      }
      if (!database.objectStoreNames.contains(FLASHCARD_DECKS_STORE)) {
        const decks = database.createObjectStore(FLASHCARD_DECKS_STORE, { keyPath: "id" });
        decks.createIndex("parentId", "parentId");
        decks.createIndex("updatedAt", "updatedAt");
      }
      if (!database.objectStoreNames.contains(FLASHCARD_NOTE_TYPES_STORE)) {
        database.createObjectStore(FLASHCARD_NOTE_TYPES_STORE, { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains(FLASHCARD_NOTES_STORE)) {
        const notes = database.createObjectStore(FLASHCARD_NOTES_STORE, { keyPath: "id" });
        notes.createIndex("deckId", "deckId");
        notes.createIndex("noteTypeId", "noteTypeId");
        notes.createIndex("updatedAt", "updatedAt");
      }
      if (!database.objectStoreNames.contains(FLASHCARD_CARDS_STORE)) {
        const cards = database.createObjectStore(FLASHCARD_CARDS_STORE, { keyPath: "id" });
        cards.createIndex("deckId", "deckId");
        cards.createIndex("noteId", "noteId");
        cards.createIndex("due", "due");
      }
      if (!database.objectStoreNames.contains(FLASHCARD_REVIEWS_STORE)) {
        const reviews = database.createObjectStore(FLASHCARD_REVIEWS_STORE, { keyPath: "id" });
        reviews.createIndex("cardId", "cardId");
        reviews.createIndex("deckId", "deckId");
        reviews.createIndex("reviewedAt", "reviewedAt");
      }
      if (!database.objectStoreNames.contains(FLASHCARD_MEDIA_STORE)) {
        const media = database.createObjectStore(FLASHCARD_MEDIA_STORE, { keyPath: "id" });
        media.createIndex("name", "name");
        media.createIndex("updatedAt", "updatedAt");
      }
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => {
        database.close();
        databasePromise = null;
      };
      resolve(database);
    };
  });

  return databasePromise;
}

export function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Falha ao acessar a biblioteca local."));
  });
}

export function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Falha ao salvar a biblioteca local."));
    transaction.onabort = () => reject(transaction.error ?? new Error("A gravação local foi cancelada."));
  });
}

export async function listLibraryBooks(): Promise<LibraryBook[]> {
  if (cloudIsConfigured()) {
    const result = await apiFetch<{ books: LibraryBook[] }>("/books");
    return result.books.map(normalizeBook);
  }
  const database = await openDatabase();
  const transaction = database.transaction([BOOKS_STORE, CHAPTERS_STORE], "readonly");
  const booksRequest = transaction.objectStore(BOOKS_STORE).getAll() as IDBRequest<StoredBook[]>;
  const chaptersRequest = transaction.objectStore(CHAPTERS_STORE).getAll() as IDBRequest<StoredChapter[]>;
  let [books, chapters] = await Promise.all([requestResult(booksRequest), requestResult(chaptersRequest)]);
  await transactionDone(transaction);

  if (!books.length && localShareAvailable()) {
    const restored = await restoreLocalShare(database);
    if (restored) ({ books, chapters } = restored);
  }
  if (books.length) scheduleLocalShare();

  return books
    .map((book) => enrichBook(book, chapters.filter((chapter) => chapter.bookId === book.id)))
    .sort((left, right) => right.updatedAt - left.updatedAt);
}

export async function getLibraryBook(bookId: string): Promise<LibraryBook | null> {
  if (cloudIsConfigured()) {
    const result = await apiFetch<{ book: LibraryBook }>(`/books/${encodeURIComponent(bookId)}`);
    return normalizeBook(result.book);
  }
  const database = await openDatabase();
  const transaction = database.transaction([BOOKS_STORE, CHAPTERS_STORE], "readonly");
  const bookRequest = transaction.objectStore(BOOKS_STORE).get(bookId) as IDBRequest<StoredBook | undefined>;
  const chapterRequest = transaction.objectStore(CHAPTERS_STORE).index("bookId").getAll(bookId) as IDBRequest<StoredChapter[]>;
  const [book, chapters] = await Promise.all([requestResult(bookRequest), requestResult(chapterRequest)]);
  await transactionDone(transaction);
  return book ? enrichBook(book, chapters) : null;
}

export async function getStoredChapter(chapterId: string): Promise<StoredChapter | null> {
  if (cloudIsConfigured()) {
    const result = await apiFetch<{ chapter: StoredChapter }>(`/chapters/${encodeURIComponent(chapterId)}`);
    return normalizeChapter(result.chapter);
  }
  const database = await openDatabase();
  const transaction = database.transaction(CHAPTERS_STORE, "readonly");
  const request = transaction.objectStore(CHAPTERS_STORE).get(chapterId) as IDBRequest<StoredChapter | undefined>;
  const chapter = await requestResult(request);
  await transactionDone(transaction);
  return chapter ?? null;
}

export async function saveBook(book: StoredBook): Promise<void> {
  if (cloudIsConfigured()) {
    const sourceBlob = book.sourceBlob;
    book.sourceBlob = null;
    await apiFetch(`/books/${encodeURIComponent(book.id)}`, { method: "PUT", body: JSON.stringify(serializableBook(book)) });
    if (sourceBlob) {
      const sourceKey = await uploadFile({
        kind: "book-source",
        bookId: book.id,
        file: sourceBlob,
        fileName: book.sourceName || "livro.epub",
      });
      book.sourceKey = sourceKey;
      await apiFetch(`/books/${encodeURIComponent(book.id)}`, { method: "PUT", body: JSON.stringify(serializableBook(book)) });
    }
    return;
  }
  const database = await openDatabase();
  const transaction = database.transaction(BOOKS_STORE, "readwrite");
  transaction.objectStore(BOOKS_STORE).put(book);
  await transactionDone(transaction);
  scheduleLocalShare();
}

export async function saveChapter(chapter: StoredChapter): Promise<void> {
  if (cloudIsConfigured()) {
    if (chapter.audioBlob) {
      chapter.audioKey = await uploadFile({
        kind: "audio",
        bookId: chapter.bookId,
        chapterId: chapter.id,
        file: chapter.audioBlob,
        fileName: chapter.audioName || "capitulo.mp3",
      });
      chapter.audioBlob = null;
      chapter.audioUrl = undefined;
    }
    await apiFetch(`/books/${encodeURIComponent(chapter.bookId)}/chapters/${encodeURIComponent(chapter.id)}`, {
      method: "PUT",
      body: JSON.stringify(serializableChapter(chapter)),
    });
    return;
  }
  const database = await openDatabase();
  const transaction = database.transaction(CHAPTERS_STORE, "readwrite");
  transaction.objectStore(CHAPTERS_STORE).put(chapter);
  await transactionDone(transaction);
  scheduleLocalShare();
}

export async function saveBookAndChapter(book: StoredBook, chapter: StoredChapter): Promise<StoredChapter> {
  if (cloudIsConfigured()) {
    await saveBook(book);
    await saveChapter(chapter);
    return chapter;
  }
  const database = await openDatabase();
  const transaction = database.transaction([BOOKS_STORE, CHAPTERS_STORE], "readwrite");
  transaction.objectStore(BOOKS_STORE).put(book);
  transaction.objectStore(CHAPTERS_STORE).put(chapter);
  await transactionDone(transaction);
  scheduleLocalShare();
  return chapter;
}

export async function saveBookWithChapters(book: StoredBook, chapters: StoredChapter[]): Promise<void> {
  if (cloudIsConfigured()) {
    await saveBook(book);
    for (const chapter of chapters) await saveChapter(chapter);
    return;
  }
  const database = await openDatabase();
  const transaction = database.transaction([BOOKS_STORE, CHAPTERS_STORE], "readwrite");
  transaction.objectStore(BOOKS_STORE).put(book);
  const chapterStore = transaction.objectStore(CHAPTERS_STORE);
  chapters.forEach((chapter) => chapterStore.put(chapter));
  await transactionDone(transaction);
  scheduleLocalShare();
}

export async function deleteStoredChapter(chapterId: string): Promise<void> {
  if (cloudIsConfigured()) {
    await apiFetch(`/chapters/${encodeURIComponent(chapterId)}`, { method: "DELETE" });
    return;
  }
  const database = await openDatabase();
  const transaction = database.transaction(CHAPTERS_STORE, "readwrite");
  transaction.objectStore(CHAPTERS_STORE).delete(chapterId);
  await transactionDone(transaction);
  scheduleLocalShare();
}

export async function deleteStoredBook(bookId: string): Promise<void> {
  if (cloudIsConfigured()) {
    await apiFetch(`/books/${encodeURIComponent(bookId)}`, { method: "DELETE" });
    return;
  }
  const database = await openDatabase();
  const transaction = database.transaction([BOOKS_STORE, CHAPTERS_STORE], "readwrite");
  transaction.objectStore(BOOKS_STORE).delete(bookId);
  const chaptersStore = transaction.objectStore(CHAPTERS_STORE);
  const chapterKeys = await requestResult(chaptersStore.index("bookId").getAllKeys(bookId));
  chapterKeys.forEach((key) => chaptersStore.delete(key));
  await transactionDone(transaction);
  scheduleLocalShare();
}

export async function fingerprintChapter(audio: Blob, text: string): Promise<string> {
  const audioDigest = await crypto.subtle.digest("SHA-256", await audio.arrayBuffer());
  const textDigest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text.trim()));
  return `${hex(audioDigest)}:${hex(textDigest)}`;
}

export function makeId(prefix: "book" | "chapter"): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export function chapterProgress(chapter: StoredChapter): number {
  const duration = chapter.duration || chapter.alignment.at(-1)?.end || 0;
  return duration > 0 ? Math.min(1, Math.max(0, chapter.position / duration)) : 0;
}

export async function alignStoredChapter(bookId: string, chapterId: string): Promise<AlignmentResponse> {
  await apiFetch<{ status: "processing" }>(`/books/${encodeURIComponent(bookId)}/chapters/${encodeURIComponent(chapterId)}/align`, {
    method: "POST",
  });
  const deadline = Date.now() + 16 * 60 * 1000;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 2_000));
    const chapter = await getStoredChapter(chapterId);
    if (!chapter) throw new Error("O capítulo deixou de existir durante o alinhamento.");
    if (chapter.status === "aligned" && chapter.alignment.length) return { characters: chapter.alignment };
    if (chapter.status === "error") throw new Error(chapter.errorMessage || "O serviço não conseguiu alinhar este capítulo.");
  }
  throw new Error("O alinhamento está demorando mais que o esperado. Ele continuará em segundo plano.");
}

export async function explainSentence(input: {
  text: string;
  contextBefore: string;
  contextAfter: string;
  language: "ja" | "en" | "ru";
}): Promise<SentenceExplanation> {
  if (!cloudIsConfigured() && import.meta.env.DEV) {
    const response = await fetch("/__japa-reader-explanations", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = await response.json() as SentenceExplanation | { error?: unknown; message?: unknown };
    if (!response.ok) throw new Error(apiErrorMessage(payload, response.status));
    return payload as SentenceExplanation;
  }
  return apiFetch<SentenceExplanation>("/explanations", { method: "POST", body: JSON.stringify(input) });
}

export async function generateVocabularyFlashcardDraft(input: {
  term: string;
  reading: string;
  meaning: string;
  sourceSentence: string;
  language: "ja" | "en" | "ru";
  avoidPatterns: string[];
}): Promise<VocabularyFlashcardDraft> {
  if (!cloudIsConfigured() && import.meta.env.DEV) {
    const response = await fetch("/__japa-reader-flashcard-drafts", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const payload = await response.json() as VocabularyFlashcardDraft | { error?: unknown; message?: unknown };
    if (!response.ok) throw new Error(apiErrorMessage(payload, response.status));
    return payload as VocabularyFlashcardDraft;
  }
  return apiFetch<VocabularyFlashcardDraft>("/flashcards/drafts", { method: "POST", body: JSON.stringify(input) });
}

export async function saveChapterProgress(chapter: StoredChapter): Promise<void> {
  if (!cloudIsConfigured()) {
    await saveChapter(chapter);
    return;
  }
  await apiFetch(`/books/${encodeURIComponent(chapter.bookId)}/chapters/${encodeURIComponent(chapter.id)}/progress`, {
    method: "PATCH",
    body: JSON.stringify({ position: chapter.position, playbackRate: chapter.playbackRate, updatedAt: chapter.updatedAt }),
  });
}

function enrichBook(book: StoredBook, chapters: StoredChapter[]): LibraryBook {
  const orderedChapters = [...chapters].sort((left, right) => left.order - right.order);
  const totalDuration = orderedChapters.reduce(
    (total, chapter) => total + (chapter.duration || chapter.alignment.at(-1)?.end || 0),
    0,
  );
  const elapsed = orderedChapters.reduce((total, chapter) => {
    const duration = chapter.duration || chapter.alignment.at(-1)?.end || 0;
    return total + Math.min(duration, Math.max(0, chapter.position));
  }, 0);

  return {
    ...book,
    language: normalizeBookLanguage(book.language),
    chapters: orderedChapters,
    completedChapters: orderedChapters.filter((chapter) => chapterProgress(chapter) >= 0.98).length,
    progress: totalDuration > 0 ? elapsed / totalDuration : 0,
    totalDuration,
  };
}

function normalizeBook(book: LibraryBook): LibraryBook {
  return {
    ...book,
    language: normalizeBookLanguage(book.language),
    sourceBlob: null,
    chapters: (book.chapters || []).map(normalizeChapter),
  };
}

function normalizeChapter(chapter: StoredChapter): StoredChapter {
  return {
    ...chapter,
    audioBlob: null,
    alignment: chapter.alignment || [],
  };
}

function serializableBook(book: StoredBook) {
  const { sourceBlob: _sourceBlob, ...record } = book;
  return record;
}

function serializableChapter(chapter: StoredChapter) {
  const { audioBlob: _audioBlob, audioUrl: _audioUrl, ...record } = chapter;
  return record;
}

async function uploadFile(input: {
  kind: "audio" | "book-source";
  bookId: string;
  chapterId?: string;
  file: Blob;
  fileName: string;
}) {
  const contentType = input.file.type || "application/octet-stream";
  const upload = await apiFetch<UploadResponse>("/uploads", {
    method: "POST",
    body: JSON.stringify({
      kind: input.kind,
      bookId: input.bookId,
      chapterId: input.chapterId,
      fileName: input.fileName,
      contentType,
      size: input.file.size,
    }),
  });
  const response = await fetch(upload.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: input.file,
  });
  if (!response.ok) {
    throw new Error(`O arquivo não pôde ser enviado para o armazenamento seguro (S3 ${response.status}).`);
  }
  return upload.key;
}

function localShareAvailable() {
  return import.meta.env.DEV && import.meta.env.VITE_LOCAL_LAN_SHARE === "1" && typeof window !== "undefined";
}

function scheduleLocalShare() {
  if (!localShareAvailable()) return;
  if (localShareTimer) window.clearTimeout(localShareTimer);
  localShareTimer = window.setTimeout(() => {
    localShareTimer = 0;
    void publishLocalShare();
  }, 600);
}

async function publishLocalShare() {
  if (localSharePublishPromise) return localSharePublishPromise;
  localSharePublishPromise = (async () => {
    const database = await openDatabase();
    const transaction = database.transaction([BOOKS_STORE, CHAPTERS_STORE], "readonly");
    const [books, chapters] = await Promise.all([
      requestResult(transaction.objectStore(BOOKS_STORE).getAll() as IDBRequest<StoredBook[]>),
      requestResult(transaction.objectStore(CHAPTERS_STORE).getAll() as IDBRequest<StoredChapter[]>),
    ]);
    await transactionDone(transaction);
    if (!books.length) return;

    const archive = new JSZip();
    const sharedBooks: SharedBook[] = books.map((book) => {
      const { sourceBlob, ...record } = book;
      const sourceEntry = sourceBlob ? `books/${book.id}/source` : undefined;
      if (sourceBlob && sourceEntry) archive.file(sourceEntry, sourceBlob);
      return { ...record, sourceEntry, sourceType: sourceBlob?.type || "application/epub+zip" };
    });
    const sharedChapters: SharedChapter[] = chapters.map((chapter) => {
      const { audioBlob, audioUrl: _audioUrl, ...record } = chapter;
      const audioEntry = audioBlob ? `books/${chapter.bookId}/chapters/${chapter.id}/audio` : undefined;
      if (audioBlob && audioEntry) archive.file(audioEntry, audioBlob);
      return { ...record, audioEntry };
    });
    archive.file("manifest.json", JSON.stringify({ version: 1, books: sharedBooks, chapters: sharedChapters } satisfies LocalShareManifest));
    const body = await archive.generateAsync({ type: "blob", compression: "STORE" });
    const response = await fetch("/__japa-reader-share", { method: "PUT", body });
    if (!response.ok) throw new Error("Falha ao compartilhar a biblioteca local.");
  })().catch(() => undefined).finally(() => {
    localSharePublishPromise = null;
  });
  return localSharePublishPromise;
}

async function restoreLocalShare(database: IDBDatabase) {
  try {
    const response = await fetch("/__japa-reader-share", { cache: "no-store" });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error("Falha ao buscar a biblioteca compartilhada.");
    const archive = await JSZip.loadAsync(await response.arrayBuffer());
    const manifestEntry = archive.file("manifest.json");
    if (!manifestEntry) return null;
    const manifest = JSON.parse(await manifestEntry.async("text")) as LocalShareManifest;
    if (manifest.version !== 1 || !Array.isArray(manifest.books) || !Array.isArray(manifest.chapters)) return null;

    const books = await Promise.all(manifest.books.map(async ({ sourceEntry, sourceType, ...book }) => {
      const sourceBytes = sourceEntry ? await archive.file(sourceEntry)?.async("uint8array") : undefined;
      return { ...book, sourceBlob: sourceBytes ? blobFromBytes(sourceBytes, sourceType) : null } satisfies StoredBook;
    }));
    const chapters = await Promise.all(manifest.chapters.map(async ({ audioEntry, ...chapter }) => {
      const audioBytes = audioEntry ? await archive.file(audioEntry)?.async("uint8array") : undefined;
      return {
        ...chapter,
        audioBlob: audioBytes ? blobFromBytes(audioBytes, chapter.audioType || "application/octet-stream") : null,
      } satisfies StoredChapter;
    }));

    const transaction = database.transaction([BOOKS_STORE, CHAPTERS_STORE], "readwrite");
    const booksStore = transaction.objectStore(BOOKS_STORE);
    const chaptersStore = transaction.objectStore(CHAPTERS_STORE);
    booksStore.clear();
    chaptersStore.clear();
    books.forEach((book) => booksStore.put(book));
    chapters.forEach((chapter) => chaptersStore.put(chapter));
    await transactionDone(transaction);
    return { books, chapters };
  } catch {
    return null;
  }
}

function blobFromBytes(bytes: Uint8Array, type = "application/octet-stream") {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return new Blob([copy.buffer], { type });
}

function hex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
